import Wrapper from './wrapper';
import {
  IDocumentCreateParams,
  IDocumentListParams,
  IDocumentUpdateParams,
  IDocumentListUnscopedResponse,
  IFileResult,
  IPageResult,
  IWrapper,
  IBulkStatusUpdateItem,
  IBulkStatusUpdateResponse,
  ISplitRequest,
  ISplitResponse,
} from './types';
import { IDocument } from '../types/my-types';

class Document {
  wrapper: IWrapper;
  /**
   * @param {Wrapper} wrapper
   */
  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  /**
   * Creates a new document in your data pool
   * @param {Object} data
   * @returns {Promise}
   */
  create(data: IDocumentCreateParams) {
    return this.wrapper.createDocument(data);
  }

  /**
   * Gets a paginated list of documents that belong to your data pool
   * @param {[Object]} params - Search parameters
   * @returns {Promise}
   */
  list(params: IDocumentListParams) {
    return this.wrapper.listDocuments(params);
  }

  /**
   * Gets a single document object
   * @param {string} id
   * @returns {Promise}
   */
  retrieve(id: string) {
    return this.wrapper.retrieveDocument(id);
  }

  listUnscoped(params: IDocumentListParams) {
    // advanced=true projects exclusion_reason / escalation_detail / document_data_id onto each
    // row — needed for the manifest's status badges and the details page header.
    return this.wrapper.client.get<IDocumentListUnscopedResponse, IDocumentListUnscopedResponse>(
      `documents?data_pool=${this.wrapper.dataPoolId}`,
      {
        params: { advanced: true, ...params },
      }
    );
  }

  retrieveAdvance(id: string) {
    return this.wrapper.client.get<IDocument, IDocument>(
      `data-pools/${this.wrapper.dataPoolId}/documents/${id}/?advanced=true`
    );
  }

  /**
   * Updates a document
   * @param {string} id
   * @param {Object} data
   * @returns {Promise}
   */
  update(id: string, data: IDocumentUpdateParams) {
    return this.wrapper.updateDocument(id, data);
  }

  /**
   * Permanently removes a document from your data pool.
   * @param {string} id
   * @returns {Promise}
   */
  del(id: string) {
    return this.wrapper.removeDocument(id);
  }

  createPages(id: string) {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/documents/${id}/pages/?advanced=true`
    );
  }

  rerenderPages(id: string): Promise<void> {
    return this.wrapper.client.post<void, void>(
      `data-pools/${this.wrapper.dataPoolId}/documents/${id}/render/`
    );
  }

  getPage(url: string) {
    return this.wrapper.client.get<IPageResult, IPageResult>(url);
  }

  getFile(id: string) {
    return this.wrapper.client.get<IFileResult, IFileResult>(
      `data-pools/${this.wrapper.dataPoolId}/documents/${id}/file/?advanced=true`
    );
  }

  /**
   * Splits a parent document into child documents. page_groups, omitted_pages,
   * page_rotations and cover_pages are all 1-indexed. See ISplitRequest.
   * @param {string} documentId obfuscated document id
   * @param {ISplitRequest} body
   * @returns {Promise<ISplitResponse>}
   */
  splitDocument(documentId: string, body: ISplitRequest): Promise<ISplitResponse> {
    return this.wrapper.client.post<ISplitResponse, ISplitResponse>(
      `data-pools/${this.wrapper.dataPoolId}/documents/${documentId}/split`,
      body
    );
  }

  bulkUpdateStatus(items: IBulkStatusUpdateItem[]): Promise<IBulkStatusUpdateResponse> {
    return this.wrapper.client.patch<IBulkStatusUpdateResponse, IBulkStatusUpdateResponse>(
      `data-pools/${this.wrapper.dataPoolId}/documents/bulk-update/?advanced=true`,
      items
    );
  }
}

export default Document;
